<template>
  <el-form ref="dataForm" :model="loadingData" :disabled="isFetchingFormData" label-position="top" status-icon>
    <el-form-item prop="referenceCategory" :label="$t('components.mesh-loader.reference-category')">
      <el-select v-model="loadingData.referenceCategory">
        <el-option v-for="category in validReferenceCategories" :key="category" :value="category" :label="$t(`enum.upload-category.${category}`)" />
      </el-select>
    </el-form-item>
    <el-form-item prop="reference" :label="$t('components.mesh-loader.reference')">
      <el-select v-model="loadingData.referenceId">
        <el-option v-for="reference in categoryReferences" :key="reference" :value="reference.uniqueKey" :label="reference.name" />
      </el-select>
    </el-form-item>

    <!-- Category Form -->
    <el-form-item prop="category" :label="$t('components.mesh-loader.category')">
      <el-select v-model="loadingData.category">
        <el-option v-for="category in validComponentCategories" :key="category" :value="category" :label="$t(`enum.upload-category.${category}`)" />
      </el-select>
    </el-form-item>

    <!-- Mesh Name -->
    <el-form-item
      prop="title"
      :label="$t('components.mesh-loader.title')"
      :rules="{
        required: true,
        message: $t('error.vuelidate.required'),
        trigger: 'blur',
      }"
    >
      <el-input v-model="loadingData.title" />
    </el-form-item>

    <!-- Upload 3D Object  -->
    <el-form-item
      prop="url"
      :label="$t('components.mesh-loader.mesh')"
      :rules="{
        required: true,
        message: $t('error.vuelidate.required'),
        trigger: 'blur',
      }"
    >
      <el-upload
        v-model:file-list="meshFiles"
        multiple
        action="#"
        drag
        list-type="picture-card"
        :before-upload="(res: any) => beforeUploadFile(UploadType.object3D, res.name)"
        :http-request="uploadObject3D"
        accept=".stl,.obj,.fbx,.mtl"
      >
        <template #default>
          <div>
            <font-awesome-icon icon="upload" class="primary big" />
            <br />
            {{ $t('components.mesh-loader.upload') }}
          </div>
        </template>
        <template #file="{ file }">
          <PreviewRenderer
            :meshes="[
              {
                filetype: getFileType(file.name),
                filename: file.name,
                url: file.url,
                raw: file.raw,
              },
            ]"
            :can-remove="true"
            :can-select="false"
            @remove-item="handleRemove(file, UploadType.object3D)"
          />
        </template>
      </el-upload>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { FileType3D, getFileType, isValidFileType, UploadCategory } from '@/types/enum/upload';
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { FileData } from '@/types/enum/file';
import { FileUploadData, MeshUploadData } from '../three/scenes/MeshLoader.vue';
import { ElMessage, ElMessageBox, UploadFile } from 'element-plus';
import { cloneDeep } from '@apollo/client/utilities';
import PreviewRenderer from '../three/scenes/PreviewRenderer.vue';
import { MeshExportData } from '@/services/api/modelService';
import { WithI18n } from '@/types/ui/Global';

export enum UploadType {
  'object3D' = 'object3D',
}

export interface ComponentMeshUploadData extends MeshUploadData {
  referenceCategory: UploadCategory;
  referenceId: string;
  referenceData: MeshExportData[];
}

@Component({
  components: {
    PreviewRenderer,
  },
  emits: ['update:loadingData'],
})
@Component
export default class OrderMeshUploadForm extends Vue {
  @Prop() loadingData!: ComponentMeshUploadData;
  @Prop({ default: 'other' }) directoryPath!: string;
  
  @Prop()
  onUploadObject3D(type3D: FileType3D, file: FileUploadData) {
    console.log('type3D',type3D)
    console.log('file',file)
    return;
  }
  @Prop()
  onRemoveObject3D(uid: number) {
    console.log('uid',uid)
    return;
  }

  // note: state for controlled el upload mesh 3d, keeping ui when clicking prev step
  meshFiles: FileData[] = [];

  UploadType = UploadType;
  UploadCategory = UploadCategory;
  FileType = FileType3D;

  validComponentCategories = [UploadCategory.Product, UploadCategory.Curve, UploadCategory.ReferenceDummy, UploadCategory.Scan];
  validReferenceCategories = [UploadCategory.Product, UploadCategory.Curve, UploadCategory.ReferenceDummy];

  getFileType = getFileType;

  get isFetchingFormData(): boolean {
    return !(this.loadingData.referenceData && this.loadingData.referenceData.length > 0);
  }

  get categoryReferences(): MeshExportData[] {
    const references = this.loadingData.referenceData.filter((item) => item.category === this.loadingData.referenceCategory);
    this.loadingData.referenceId = '';
    if (references.length > 0) this.loadingData.referenceId = references[0].uniqueKey;
    return references;
  }

  beforeUploadFile(type: UploadType, fileName: string): boolean {
    switch (type) {
      case UploadType.object3D:
        if (isValidFileType(fileName)) {
          return true;
        }
        break;
    }
    ElMessage.error((this as WithI18n).$t('components.mesh-loader.wrongType'));
    return false;
  }

  async uploadObject3D(res: {file:File & {uid:number}}): Promise<boolean> {
    const rawFile = res.file as File
    const fileType = getFileType(res.file.name) as FileType3D;
    const url = URL.createObjectURL(res.file);

  if (fileType !== FileType3D.NONE && url) {
    this.loadingData.url = url;
    this.loadingData.fileType = fileType;

    this.onUploadObject3D(fileType, {
      name: rawFile.name,
      url: url,
      uid: res.file.uid,
    });
  }

  return true;
}

  handleRemove(file: UploadFile, uploadType: UploadType): void {
    ElMessageBox.confirm((this as WithI18n).$t('components.mesh-loader.deleteMessage'), 'info', {
      confirmButtonText: (this as WithI18n).$t('components.mesh-loader.yes'),
      cancelButtonText: (this as WithI18n).$t('components.mesh-loader.no'),
      type: 'warning',
    })
      .then(async (confirm) => {
        if (confirm) {
          if (uploadType === UploadType.object3D) {
            this.onRemoveObject3D(file?.uid);
            const newMeshFiles = cloneDeep(this.meshFiles).filter((f) => f.uid !== file?.uid);
            this.meshFiles = newMeshFiles;
          }
        }
      })
      .catch(() => {
        //
      });
  }
}
</script>
